import { useState, useEffect } from 'react';
import Stack from '@mui/material/Stack';
import { useSearchParams } from 'react-router-dom';
import Filters from '@FormFunctionalDisplay/Filters';
import SearchFormRow from '@FormRepresentationalDisplay/SearchFormRow';
import Pagination from '@mui/material/Pagination';
import LeapFrogLoader from '@CommonRepresentationalDisplay/Loaders/LeapFrogLoader';
import ErrorHandler from '@Root/ErrorHandler';
import KaiziqResolveConnectionService from '@Helpers/URLHelpers/KaiziqResolveConnectionService';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import '@Partials/_ComStyles.scss';
import '@Partials/_SimpleStyles.scss';
import './FormSearchPage.scss';


export default function FormSearchPage(props) {

    const [kaiziqResolveConnectionList, setKaiziqResolveConnectionList] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [cursor, setCursor] = useState('auto');
    const [searchParams] = useSearchParams();
    const errorHandler = new ErrorHandler();
    const kaiziqResolveConnectionService = new KaiziqResolveConnectionService();
    const defaultFilters = {
        'kaiziqResolveConnectionFilter': {
            'visibilityList': ['public', 'private'],
            'connectionLiveList': [true],
            'sortBy': 'createTimeStamp',
            'sortAsc': false
        },
        'page': 0,
        'size': 10
    }
    const [filters, setFilters] = useState(defaultFilters)
    const className = 'formSearchPage';

    useEffect(() => {
        let searchFormQuery = searchParams.get('searchFormQuery');
        if(!!searchFormQuery) {
            let filtersObj = filters;
            filtersObj.kaiziqResolveConnectionFilter.searchQuery = searchFormQuery;
            filtersObj.size = 10;
            onFilterChange(filtersObj);
            handleSearchClickWithValue(filtersObj);
        }
    }, [])

    const onFilterChange = (filterValue) => {
        filterValue.size = 10;
        setFilters(filterValue);
    }

    const handleSearchClickWithValue = async(filtersObj) => {
        setCursor('wait');
        try {
            let kaiziqResolveConnectionObj = await kaiziqResolveConnectionService.getListFiltered(filtersObj);
            kaiziqResolveConnectionObj = JSON.parse(kaiziqResolveConnectionObj.body);
            let result = kaiziqResolveConnectionObj.result;
            setKaiziqResolveConnectionList(result.connectionList);
            setTotalItems(result.totalItems);
            setTotalPages(result.totalPages);
        } catch(err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    }

    const handleSearchClick = async() => {
        setCursor('wait');
        try {
            let filterObj = {...filters};
            filterObj.kaiziqResolveConnectionFilter.visibilityList = ['public'];
            let kaiziqResolveConnectionObj = await kaiziqResolveConnectionService.getListFiltered(filterObj);
            kaiziqResolveConnectionObj = JSON.parse(kaiziqResolveConnectionObj.body);
            let result = kaiziqResolveConnectionObj.result;
            setKaiziqResolveConnectionList(result.connectionList);
            setTotalItems(result.totalItems);
            setTotalPages(result.totalPages);
        } catch(err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    }

    const handleNextPageClick = async(filtersObj) => {
        setCursor('wait');
        try {
            let kaiziqResolveConnectionObj = await kaiziqResolveConnectionService.getListFiltered(filtersObj);
            kaiziqResolveConnectionObj = JSON.parse(kaiziqResolveConnectionObj.body);
            let result = kaiziqResolveConnectionObj.result;
            setKaiziqResolveConnectionList(result.connectionList);
            setTotalItems(result.totalItems);
            setTotalPages(result.totalPages);
        } catch(err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    }

    const handlePaginationClick = (e, v) => {
        setCurrentPage(v);
        let filtersObj = filters;
        filtersObj.page = v - 1;
        filtersObj.size = 10;
        setFilters(filtersObj);
        handleNextPageClick(filtersObj);
    }

    return (
        <div className={className} style={{cursor:cursor}}>
            <div className={className + '__inner'}>
                <div className={className + '__inner__header'}>
                    <div className={className + '__inner__header__badge'}>
                        📝 Form Search
                    </div>
                    <h1 className={className + '__inner__header__title'}>
                        Find Your Form
                    </h1>
                    <p className={className + '__inner__header__subtitle'}>
                        Search and select a form to report your ticket
                    </p>
                </div>

                <div className={className + '__inner__filters'}>
                    <Filters 
                        filters={filters} 
                        defaultFilters={defaultFilters} 
                        onFilterChange={onFilterChange}
                        handleSearch={handleSearchClick} 
                        cursor={cursor}
                    />
                </div>

                <div className={className + '__inner__content'}>
                    {cursor === 'wait' && (
                        <div className={className + '__inner__loader'}>
                            <div className={className + '__inner__loader__spinner'}>
                                <LeapFrogLoader/>
                            </div>
                            <p className={className + '__inner__loader__text'}>Loading forms...</p>
                        </div>
                    )}

                    {cursor !== 'wait' && kaiziqResolveConnectionList?.length === 0 && (
                        <div className={className + '__inner__emptyState'}>
                            <SearchOutlinedIcon className={className + '__inner__emptyState__icon'}/>
                            <h3 className={className + '__inner__emptyState__title'}>
                                No Forms Found
                            </h3>
                            <p className={className + '__inner__emptyState__description'}>
                                Try adjusting your search criteria or filters
                            </p>
                        </div>
                    )}

                    {cursor !== 'wait' && kaiziqResolveConnectionList?.length > 0 && (
                        <div className={className + '__inner__list'}>
                            {kaiziqResolveConnectionList.map((kaiziqResolveConnection) => (
                                <SearchFormRow
                                    key={kaiziqResolveConnection.id}
                                    kaiziqResolveConnection={kaiziqResolveConnection}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {cursor !== 'wait' && kaiziqResolveConnectionList?.length > 0 && (
                    <div className={className + '__inner__pagination'}>
                        <div className={className + '__inner__pagination__flex'}>
                            <Stack spacing={1}>
                                <Pagination
                                    count={totalPages}
                                    page={currentPage}
                                    showFirstButton
                                    showLastButton
                                    onChange={handlePaginationClick}
                                    className={className + '__inner__pagination__flex__component'}
                                />
                            </Stack>
                            <div className={className + '__inner__pagination__flex__total'}>
                                Total Items: {totalItems}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
